(function (root, factory) {
  'use strict';
  var exported = factory();
  if (typeof module === 'object' && module.exports) module.exports = exported;
  root.FBGroupGiftsPlan = exported;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  function create(options) {
    var disposed = false, generation = 0, pending = null;
    var state = { saving: false, error: null, notice: '', undo: null };
    function changed() { if (!disposed && options.onChange) options.onChange(state); }
    function keyed(scope, body) { body.idempotencyKey = options.key(scope, body); return body; }
    function ids() { return options.getGroup().plan.items.map(function (item) { return item.suggestionId; }); }
    async function run(intent, prepare, perform) {
      if (disposed || state.saving) return;
      if (!pending || pending.intent !== intent) pending = Object.assign({ intent: intent }, prepare());
      var operation = pending, current = generation;
      function active() { return !disposed && current === generation; }
      function accept(response) { if (active()) options.onGroup(response.group); }
      state.saving = true; state.error = null; state.notice = '';
      if (!intent.startsWith('restore:')) state.undo = null;
      changed();
      try {
        await perform(operation, accept, active);
        if (active()) pending = null;
      } catch (error) {
        if (!active()) return;
        state.error = error;
        if (operation.added) state.error = Object.assign(new Error('The new option was added to the shortlist, but the chosen gifts could not be saved. Try again.'), { code: error.code, status: error.status });
        if (error.code === 'EDIT_CONFLICT') {
          pending = null;
          state.error = Object.assign(new Error((operation.added ? 'The new option was added to the shortlist. ' : '') + 'These choices changed in another session. Review the latest choices and try again.'), { code: error.code, status: error.status });
          try { accept(await options.request('')); }
          catch (reloadError) { if (active()) state.error = Object.assign(new Error('These choices changed in another session. Couldn’t load the latest choices; refresh before trying again.'), { code: reloadError.code, status: reloadError.status }); }
        }
      } finally { if (active()) { state.saving = false; changed(); } }
    }
    function restore(suggestion) {
      return run('restore:' + suggestion.id + ':' + suggestion.version, function () {
        return { body: keyed('restore', { expectedSuggestionVersion: suggestion.version }) };
      }, async function (operation, accept, active) {
        accept(await options.request('/suggestions/' + encodeURIComponent(suggestion.id) + '/restore', operation.body));
        if (active()) { state.notice = 'Idea restored to Other ideas.'; state.undo = null; }
      });
    }
    return {
      state: state,
      choose: function (suggestionId, chosen) {
        return run('choose:' + suggestionId + ':' + chosen, function () {
          var choices = ids().filter(function (id) { return id !== suggestionId; });
          if (chosen) choices.push(suggestionId);
          return { body: keyed('plan', { expectedVersion: options.getGroup().plan.version, suggestionIds: choices }) };
        }, async function (operation, accept, active) {
          accept(await options.request('/plan', operation.body, 'PUT'));
          if (active()) state.notice = 'Choices saved.';
        });
      },
      restorePrevious: function (suggestionIds) {
        return run('previous:' + JSON.stringify(suggestionIds), function () {
          return { body: keyed('previous-plan', { expectedVersion: options.getGroup().plan.version, suggestionIds: suggestionIds.slice() }) };
        }, async function (operation, accept, active) {
          accept(await options.request('/plan', operation.body, 'PUT'));
          if (active()) state.notice = 'Previous choices saved. Confirm the gifts and review a new delivery total before ordering.';
        });
      },
      confirm: function (expectedVersion) {
        var version = expectedVersion == null ? options.getGroup().plan.version : expectedVersion;
        return run('confirm:' + version, function () {
          return { body: keyed('confirm-plan', { expectedVersion: version }) };
        }, async function (operation, accept, active) {
          var response = await options.request('/plan/confirm', operation.body);
          accept(response);
          if (!active()) return;
          var plan = response.group && response.group.plan;
          if (!plan || plan.version !== operation.body.expectedVersion || plan.confirmedVersion !== operation.body.expectedVersion)
            throw new Error('We couldn’t confirm these choices. Review the latest shortlist before confirming.');
          state.notice = 'Chosen gifts confirmed. Delivery and the final total still need review; nothing has been ordered.';
        });
      },
      archive: function (suggestion) {
        return run('archive:' + suggestion.id + ':' + suggestion.version, function () {
          return { body: keyed('archive', { expectedSuggestionVersion: suggestion.version, expectedPlanVersion: options.getGroup().plan.version }) };
        }, async function (operation, accept, active) {
          accept(await options.request('/suggestions/' + encodeURIComponent(suggestion.id) + '/archive', operation.body));
          if (!active()) return;
          state.notice = 'Idea removed. Its discussion is preserved in Removed ideas.';
          // The mutation returns a current group, whose active list omits archives.
          // Check the archived version before offering Undo of this exact removal.
          try {
            var result = await options.request('/suggestions?state=archived');
            if (!active()) return;
            var current = result.suggestions.find(function (item) { return item.id === suggestion.id; });
            if (current && current.version === suggestion.version + 1 && current.permissions.canRestore) state.undo = { id: current.id, version: current.version };
          } catch (error) {
            if (['UNAUTHENTICATED', 'FORBIDDEN', 'NOT_FOUND'].includes(error.code) || [401, 403, 404].includes(error.status)) throw error;
            if (active()) state.notice += ' Open Removed ideas to restore it.';
          }
        });
      },
      restore: restore,
      undo: function () { if (state.undo) return restore(state.undo); },
      replace: function (oldId, variant) {
        return run('replace:' + oldId + ':' + JSON.stringify(variant), function () {
          return { originalIds: ids(), expectedVersion: options.getGroup().plan.version, addBody: keyed('suggestion', Object.assign({}, variant)) };
        }, async function (operation, accept, active) {
          if (!operation.originalIds.includes(oldId)) throw Object.assign(new Error('The original gift is no longer chosen.'), { code: 'EDIT_CONFLICT' });
          if (!operation.added) {
            var result = await options.request('/suggestions', operation.addBody);
            if (!active()) return;
            accept(result);
            if (result.change.currentState !== 'active') throw new Error('This idea has since been removed. Review the shortlist before changing size.');
            operation.added = result.change.suggestionId;
            var choices = operation.originalIds.map(function (id) { return id === oldId ? operation.added : id; });
            choices = choices.filter(function (id, index) { return choices.indexOf(id) === index; });
            operation.planBody = keyed('plan', { expectedVersion: operation.expectedVersion, suggestionIds: choices });
          }
          if (!active()) return;
          accept(await options.request('/plan', operation.planBody, 'PUT'));
          if (active()) state.notice = 'New size chosen. The original idea and discussion remain in Other ideas.';
        });
      },
      dispose: function () { disposed = true; generation++; pending = null; state.error = null; state.undo = null; state.notice = ''; state.saving = false; }
    };
  }
  return { create: create };
});
